import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowUpRight,
  ChevronRight,
  Flame,
  Heart,
  Coins,
  Receipt,
  ShieldAlert,
  Ticket,
  TrendingUp,
  Trophy,
  Users,
  Wallet,
} from "lucide-react";
import {
  formatCash,
  isPartneredHouse,
  LEDGER_TONE,
  ledgerLabel,
  partnerCount,
  pulseStatValue,
  settlementHouseDisplay,
  sortReadinessChecksForDisplay,
} from "../../utils/operatorConsoleHelpers";
import {
  canUserRunConsoleTreasuryMove,
  isBetHousePartnershipReadyForConsole,
} from "../../utils/betHousePartners";
import { formatAppDate, formatAppDateTime } from "../../utils/appTimeFormat";
import HouseAnalyticsBoard from "./HouseAnalyticsBoard";
import TreasuryVaultBoard from "./TreasuryVaultBoard";
import "../../operator-finance.css";

const StatTile = ({ label, value, sub, accent, needOdds }) => (
  <div
    className={`oc-stat ${
      needOdds ? "oc-stat-need-odds" : accent ? "oc-stat-accent" : ""
    }`}
  >
    <em>{label}</em>
    <strong className="tabular-nums">{value}</strong>
    {sub && <span>{sub}</span>}
  </div>
);

const ControlTile = ({ icon: Icon, label, value, hint, disabled, to, onClick }) => {
  const body = (
    <>
      <span className="oc-control-icon">
        <Icon size={16} />
      </span>
      <div>
        <em>{label}</em>
        <strong>{value}</strong>
        {hint && <p>{hint}</p>}
      </div>
      {!disabled && <ChevronRight size={14} className="oc-control-chevron" />}
    </>
  );

  if (onClick && !disabled) {
    return (
      <button type="button" className="oc-control-tile" onClick={onClick}>
        {body}
      </button>
    );
  }

  if (to && !disabled) {
    return (
      <Link to={to} className="oc-control-tile">
        {body}
      </Link>
    );
  }

  return (
    <div className={`oc-control-tile ${disabled ? "oc-control-tile-soon" : ""}`}>
      {body}
      {disabled && <span className="oc-soon-pill">Soon</span>}
    </div>
  );
};

export const OperatorPulsePanel = ({ bet, dashboard, onGoTreasury, bettorsPath, ticketsPath, viewersPath, followersPath }) => {
  const activity = dashboard?.activity || {};
  const company = dashboard?.company || {};
  const health = dashboard?.operatorHealth || {};
  const readiness = bet?.goLiveReadiness;
  const isDraft = String(bet?.status || "").toLowerCase() === "draft";
  const hasDashboard = Boolean(dashboard && Object.keys(dashboard).length);
  const houseId = bet?._id;
  const readinessChecks = useMemo(
    () => sortReadinessChecksForDisplay(readiness?.checks, 6),
    [readiness?.checks]
  );
  const readinessStats = useMemo(() => {
    const list = Array.isArray(readiness?.checks) ? readiness.checks : [];
    const total = list.length;
    const passed = list.filter((c) => Boolean(c.ok ?? c.pass)).length;
    return { passed, total };
  }, [readiness?.checks]);
  const stat = (value, draftFallback = 0) =>
    pulseStatValue(value, { isDraft, hasDashboard, draftFallback });

  return (
    <section className="oc-panel">
      <header className="oc-panel-head">
        <h2>Your house at a glance</h2>
        <p>Performance, league intel, and how your audience turns into play.</p>
      </header>

      {isDraft && readiness && (
        <div className="oc-readiness">
          <div className="oc-readiness-head">
            <AlertTriangle size={14} />
            <strong>Go-live readiness</strong>
            <span className={readiness.canActivate ? "oc-tag-success" : "oc-tag-warning"}>
              {readiness.canActivate ? "Ready" : "Incomplete"}
            </span>
            {readinessStats.total > 0 ? (
              <span className="oc-readiness-count">
                {readinessStats.passed}/{readinessStats.total} passed
              </span>
            ) : null}
          </div>
          <ul className="oc-readiness-list">
            {readinessChecks.map((check) => (
              <li
                key={check.id}
                className={check.ok ? "oc-readiness-pass" : "oc-readiness-fail"}
              >
                <span>{check.ok ? "✓" : "○"}</span>
                <div className="oc-readiness-copy">
                  <strong>{check.title}</strong>
                  {!check.ok && check.detail ? <em>{check.detail}</em> : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(health.autoPauseImminent || health.fixturesNeedingOdds > 0) && (
        <div className="oc-alert">
          <Flame size={14} />
          <div>
            <strong>Needs attention</strong>
            <p>
              {health.autoPauseImminent && "Bettable fixture pool is below minimum. "}
              {health.fixturesNeedingOdds > 0 &&
                `${health.fixturesNeedingOdds} fixture(s) still need odds.`}
            </p>
          </div>
        </div>
      )}

      {houseId ? (
        <HouseAnalyticsBoard houseId={houseId} dashboard={dashboard} />
      ) : null}

      <div className="oc-control-grid">
        <ControlTile
          icon={Users}
          label="Bettors"
          value={stat(dashboard?.customers?.totalUnique)}
          hint="Unique customers"
          to={bettorsPath}
        />
        <ControlTile
          icon={Ticket}
          label="Tickets"
          value={stat(activity.openTickets)}
          hint="Open positions"
          to={ticketsPath}
        />
        <ControlTile
          icon={TrendingUp}
          label="Viewers"
          value={stat(company.viewers)}
          hint={`${stat(company.totalViews, 0)} views`}
          to={viewersPath}
        />
        <ControlTile
          icon={Heart}
          label="Followers"
          value={stat(company.followers)}
          hint="Follow & unfollow events"
          to={followersPath}
        />
        <ControlTile
          icon={Wallet}
          label="Treasury"
          value={formatCash(dashboard?.treasury?.availableBalance ?? bet?.amountAvailable, { compact: true })}
          hint="House balance"
          onClick={onGoTreasury}
        />
      </div>
    </section>
  );
};

export const OperatorTreasuryPanel = ({
  dashboard,
  bet,
  operatorUserId,
  onOpenDeposit,
  onOpenWithdraw,
}) => {
  const canOperateTreasury = useMemo(
    () => canUserRunConsoleTreasuryMove(bet, operatorUserId),
    [bet, operatorUserId]
  );
  const partnershipReady = useMemo(
    () => isBetHousePartnershipReadyForConsole(bet),
    [bet]
  );
  const partnered = isPartneredHouse(bet);

  const openDeposit = () => {
    if (!bet?._id) return;
    onOpenDeposit?.();
  };

  const openWithdraw = () => {
    if (!bet?._id) return;
    onOpenWithdraw?.();
  };

  const treasuryActions = canOperateTreasury ? (
    <>
      {!partnershipReady ? (
        <div className="oc-alert oc-alert-warning">
          <AlertTriangle size={14} />
          <div>
            <strong>Partners pending</strong>
            <p>All co-owners must accept before treasury deposits or withdrawals.</p>
          </div>
        </div>
      ) : null}

      {partnered && partnershipReady ? (
        <div className="oc-treasury-partner-note">
          <Users size={14} />
          <p>
            Partnered house · {partnerCount(bet)} capital partners. Deposits and withdrawals
            split equally across wallets.
          </p>
        </div>
      ) : null}

      <div className="oc-treasury-actions">
        <button
          type="button"
          onClick={openDeposit}
          disabled={!bet?._id}
          title="Deposit Cash into house treasury"
        >
          Deposit
        </button>
        <button
          type="button"
          onClick={openWithdraw}
          disabled={!bet?._id}
          title="Withdraw Cash from house treasury"
        >
          Withdraw
        </button>
      </div>
    </>
  ) : (
    <div className="oc-alert oc-treasury-readonly">
      <AlertTriangle size={14} />
      <div>
        <strong>Creator only</strong>
        <p>Only the house creator can initiate deposits and withdrawals from the treasury.</p>
      </div>
    </div>
  );

  return (
    <section className="oc-panel" id="treasury">
      <header className="oc-panel-head">
        <h2>Treasury</h2>
        <p>Vault composition, daily cashflow, and the full house ledger.</p>
      </header>

      <TreasuryVaultBoard
        houseId={bet?._id}
        dashboard={dashboard}
        actions={treasuryActions}
      />
    </section>
  );
};

export const OperatorBettorsPanel = ({ dashboard, houseId }) => {
  const top = dashboard?.customers?.topBettors || [];
  const allPath = houseId ? `/app/bethouse/manage/${houseId}/bettors` : null;

  return (
    <section className="oc-panel">
      <header className="oc-panel-head">
        <h2>Bettors</h2>
        <p>Top customers by stake volume on this house.</p>
      </header>

      {top.length === 0 ? (
        <p className="oc-empty-copy">No bettor activity yet.</p>
      ) : (
        <ul className="oc-rank-list">
          {top.map((row, index) => (
            <li key={row.userId || index} className="oc-rank-row">
              <span className="oc-rank-num">#{index + 1}</span>
              <div>
                <strong>{row.username || "Bettor"}</strong>
                <span>{row.ticketCount} tickets</span>
              </div>
              <em className="tabular-nums">{formatCash(row.totalStaked, { compact: true })}</em>
            </li>
          ))}
        </ul>
      )}

      {allPath ? (
        <Link to={allPath} className="oc-link-btn">
          View all bettors
          <ArrowUpRight size={13} />
        </Link>
      ) : (
        <button type="button" className="oc-link-btn" disabled>
          View all bettors
          <ArrowUpRight size={13} />
        </button>
      )}
    </section>
  );
};

const ACTIVITY_LEDGER_ICONS = {
  DEPOSIT: Wallet,
  WITHDRAWAL: ArrowUpRight,
  STAKE_RECEIVED: Ticket,
  PAYOUT_MADE: Trophy,
  PLATFORM_COMMISSION: Receipt,
  LIABILITY_LOCKED: ShieldAlert,
  LIABILITY_RELEASED: ShieldAlert,
};

const ActivityLedgerRow = ({ entry }) => {
  const meta = LEDGER_TONE[entry.type] || { sign: "", tone: "muted" };
  const Icon = ACTIVITY_LEDGER_ICONS[entry.type] || Coins;
  const iconTone =
    meta.tone === "credit" ? "credit" : meta.tone === "debit" ? "debit" : "";
  const noteBits = [entry.note, entry.reference && !entry.note ? entry.reference : null].filter(
    Boolean
  );

  return (
    <li className="oc-fin-ledger-row">
      <span className={`oc-fin-ledger-icon${iconTone ? ` oc-fin-ledger-icon-${iconTone}` : ""}`}>
        <Icon size={12} />
      </span>
      <div className="oc-fin-ledger-copy">
        <strong>{ledgerLabel(entry.type)}</strong>
        <span>
          {formatAppDateTime(entry.createdAt, {
            includeWeekday: false,
            includeSeconds: false,
          })}
          {noteBits.length ? ` · ${noteBits.join(" · ")}` : ""}
        </span>
      </div>
      <em className={`oc-fin-ledger-amount tabular-nums oc-fin-ledger-amount-${meta.tone}`}>
        {meta.sign}
        {formatCash(entry.amount, { compact: true })}
      </em>
    </li>
  );
};

export const OperatorActivityPanel = ({
  dashboard,
  onGoTreasury,
  ticketsPath,
}) => {
  const activity = dashboard?.activity || {};
  const settlements = dashboard?.recentSettlements || [];
  const ledger = dashboard?.recentLedger || [];

  const tapeSummary = useMemo(() => {
    let houseNet = 0;
    let wins = 0;
    let losses = 0;
    for (const row of settlements) {
      const display = settlementHouseDisplay(row);
      if (display.tone === "credit") losses += 1;
      else if (display.tone === "debit") wins += 1;
      const signed =
        display.sign === "+"
          ? display.amount
          : display.sign === "-"
          ? -display.amount
          : 0;
      houseNet += signed;
    }
    return { houseNet, wins, losses, count: settlements.length };
  }, [settlements]);

  return (
    <section className="oc-panel oc-activity-panel">
      <header className="oc-activity-head">
        <div>
          <span className="oc-activity-kicker">Tape</span>
          <strong className="oc-activity-title">House activity</strong>
          <p className="oc-activity-caption">
            Settlements and treasury moves, newest first.
          </p>
        </div>
      </header>

      <div className="oc-activity-hero">
        <div className="oc-activity-stat">
          <em>Open tickets</em>
          <strong className="tabular-nums">{activity.openTickets ?? "—"}</strong>
        </div>
        <div className="oc-activity-stat">
          <em>Settled (all time)</em>
          <strong className="tabular-nums">{activity.settledTickets ?? "—"}</strong>
        </div>
        <div className="oc-activity-stat">
          <em>Tape net</em>
          <strong
            className={`tabular-nums ${
              tapeSummary.houseNet > 0
                ? "oc-activity-up"
                : tapeSummary.houseNet < 0
                ? "oc-activity-down"
                : ""
            }`}
          >
            {settlements.length
              ? `${tapeSummary.houseNet >= 0 ? "+" : ""}${formatCash(tapeSummary.houseNet, {
                  compact: true,
                })}`
              : "—"}
          </strong>
          {settlements.length ? (
            <span>
              {tapeSummary.losses} house win{tapeSummary.losses === 1 ? "" : "s"} ·{" "}
              {tapeSummary.wins} payout{tapeSummary.wins === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>
        <div className="oc-activity-stat">
          <em>Ledger events</em>
          <strong className="tabular-nums">{ledger.length || "—"}</strong>
          <span>Recent treasury moves</span>
        </div>
      </div>

      <div className="oc-activity-tapes">
        <section className="oc-fin-card oc-activity-card">
          <header className="oc-fin-card-head">
            <div>
              <span className="oc-fin-kicker">Settlement tape</span>
              <p className="oc-fin-caption">Last settled tickets · house P&amp;L</p>
            </div>
            {ticketsPath ? (
              <Link to={ticketsPath} className="oc-activity-card-link">
                Tickets desk
                <ArrowUpRight size={12} />
              </Link>
            ) : null}
          </header>

          {settlements.length === 0 ? (
            <p className="oc-fin-empty-copy">No settled tickets yet.</p>
          ) : (
            <ul className="oc-fin-ledger-list">
              {settlements.map((row) => {
                const display = settlementHouseDisplay(row);
                return (
                  <li key={row.ticketId} className="oc-fin-ledger-row oc-activity-settle-row">
                    <span
                      className={`oc-fin-ledger-icon oc-fin-ledger-icon-${display.tone}`}
                    >
                      <Ticket size={12} />
                    </span>
                    <div className="oc-fin-ledger-copy">
                      <strong>{row.username || "Bettor"}</strong>
                      <span>
                        {display.label} · stake{" "}
                        {formatCash(row.stake, { compact: true })}
                      </span>
                    </div>
                    <div className="oc-activity-settle-side">
                      <em
                        className={`oc-fin-ledger-amount tabular-nums oc-fin-ledger-amount-${display.tone}`}
                      >
                        {display.sign}
                        {formatCash(display.amount, { compact: true })}
                      </em>
                      <span>{formatAppDate(row.settledAt)}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="oc-fin-card oc-activity-card">
          <header className="oc-fin-card-head">
            <div>
              <span className="oc-fin-kicker">Ledger tape</span>
              <p className="oc-fin-caption">Newest treasury moves</p>
            </div>
            {onGoTreasury ? (
              <button type="button" className="oc-activity-card-link" onClick={onGoTreasury}>
                Full ledger
                <ArrowUpRight size={12} />
              </button>
            ) : null}
          </header>

          {ledger.length === 0 ? (
            <p className="oc-fin-empty-copy">No ledger activity yet.</p>
          ) : (
            <ul className="oc-fin-ledger-list">
              {ledger.map((entry, idx) => (
                <ActivityLedgerRow
                  key={`${entry.type}-${entry.createdAt}-${idx}`}
                  entry={entry}
                />
              ))}
            </ul>
          )}
        </section>
      </div>
    </section>
  );
};

export const OperatorComingSoonPanel = ({ section, bet, dashboard }) => {
  const hints = {
    loan: "Request and repay house credit lines against your treasury.",
    partners: "Invite co-owners and manage capital partner approvals.",
    risk: "Live exposure map, fixture suspensions, and liability board.",
  };

  const partners = bet?.users?.filter((u) => u.role === "owner" || u.role === "admin") || [];
  const health = dashboard?.operatorHealth;

  return (
    <section className="oc-panel oc-panel-soon">
      <header className="oc-panel-head">
        <h2>{section.label}</h2>
        <p>{hints[section.id] || "Full web view coming soon."}</p>
      </header>

      <div className="oc-soon-card">
        <Coins size={22} />
        <strong>Mobile-ready · Web preview</strong>
        <p>This section is fully available in the BetBond app today. Web controls are on the way.</p>
      </div>

      {section.id === "partners" && partners.length > 0 && (
        <ul className="oc-partner-list">
          {partners.map((p) => (
            <li key={p._id || p.userId?._id || p.userId}>
              <strong>{p.userId?.name || "Partner"}</strong>
              <span>{p.role} · {p.status || "accepted"}</span>
            </li>
          ))}
        </ul>
      )}

      {section.id === "risk" && health && (
        <div className="oc-stat-grid oc-stat-grid-compact">
          <StatTile label="Suspended fixtures" value={dashboard?.fixtures?.suspended ?? 0} accent />
          <StatTile label="Closing soon" value={dashboard?.fixtures?.closingSoon ?? 0} />
          <StatTile
            label="Need odds"
            value={health.fixturesNeedingOdds ?? 0}
            needOdds={(health.fixturesNeedingOdds ?? 0) > 0}
          />
          <StatTile label="Bettable pool" value={health.bettableUpcomingFixtures ?? "—"} />
        </div>
      )}
    </section>
  );
};
